import * as React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Wallet, PlayCircle, User, Settings as SettingsIcon, Plus, Download, Trash2, Lock, RefreshCw, AlertTriangle, CheckCircle2, CalendarDays, ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail, BACKEND } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import DetailDrawer from "@/components/DetailDrawer";
import { fmtCents, mondayOf, isoDate } from "@/lib/time";
import RunDetailDrawer from "@/components/pay/RunDetailDrawer";
import { SkeletonTable } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

export default function Pay() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "runs";
  const { has } = useCompany();
  const canManage = has("pay.manage");
  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="pay-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
          <Wallet size={17} />
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Pay</div>
          <div className="text-[11.5px] text-brand-muted">Gross pay engine · timesheets → runs → CSV. Not tax.</div>
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-brand-rule mb-4">
        {canManage && (
          <Tab v="runs" cur={tab} setV={(v)=>setParams({tab:v})} icon={<PlayCircle size={13}/>}>Runs</Tab>
        )}
        <Tab v="my" cur={tab} setV={(v)=>setParams({tab:v})} icon={<User size={13}/>}>My pay</Tab>
        {canManage && (
          <Tab v="settings" cur={tab} setV={(v)=>setParams({tab:v})} icon={<SettingsIcon size={13}/>}>Settings</Tab>
        )}
        {canManage && (
          <PayV2LinkTab />
        )}
      </div>
      {tab === "runs" && canManage && <RunsList />}
      {tab === "my" && <MyPay />}
      {tab === "settings" && canManage && <PaySettings />}
      {tab === "runs" && !canManage && <div className="text-[12px] text-brand-muted">You need <span className="mono-num chip">pay.manage</span> to run pay.</div>}
    </div>
  );
}

function Tab({ v, cur, setV, children, icon }) {
  const active = v === cur;
  return (
    <button onClick={() => setV(v)}
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 flex items-center gap-1.5 ${active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            data-testid={`pay-tab-${v}`}>
      {icon}{children}
    </button>
  );
}

function PayV2LinkTab() {
  const { companyId } = useParams();
  return (
    <Link to={`/c/${companyId}/m/pay/v2`}
          className="ml-auto px-3 py-2 text-[12.5px] font-medium border-b-2 border-transparent text-brand-muted hover:text-brand-violet flex items-center gap-1.5"
          data-testid="pay-tab-v2">
      <CalendarDays size={13} /> Periods (v2) <span className="chip chip-violet mono-num text-[9.5px]">new</span>
    </Link>
  );
}

// ---------------- RUNS ----------------

function RunsList() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const [rows, setRows] = React.useState(null);
  const [open, setOpen] = React.useState(null); // run object
  const [creating, setCreating] = React.useState(false);
  const canApprove = has("pay.approve");

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/pay/runs`);
      setRows(data.runs);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type;
      if (t && t.startsWith("pay_run.")) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  async function newRun() {
    if (creating) return;
    // Default period_start = Monday of current week
    const period_start = isoDate(mondayOf());
    setCreating(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/pay/runs`, { period_start });
      toast.success(`Created — ${data.totals.line_count} lines`);
      load();
      setOpen(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setCreating(false); }
  }

  if (!rows) return (
    <div className="card-panel overflow-hidden" data-testid="pay-runs-loading">
      <SkeletonTable rows={5} cols={7} />
    </div>
  );

  return (
    <div data-testid="pay-runs">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] text-brand-muted">
          <span className="mono-num text-brand-ink">{rows.length}</span> runs · latest first
        </div>
        <button className="btn-violet" onClick={newRun} disabled={creating} data-testid="pay-new-run">
          <Plus size={13}/> {creating ? "Creating…" : "New run (this week)"}
        </button>
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="pay-runs-table">
            <thead>
              <tr>
                <th style={{width:"20%"}}>Period</th>
                <th style={{width:"12%"}}>Status</th>
                <th style={{width:"10%"}} className="text-right">Lines</th>
                <th style={{width:"12%"}} className="text-right">Hours</th>
                <th style={{width:"10%"}} className="text-right">OT</th>
                <th style={{width:"14%"}} className="text-right">Gross</th>
                <th style={{width:"22%"}} className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`pay-run-row-${r.id}`} data-clickable="true" onClick={() => setOpen(r)}>
                  <td className="mono-num text-[12px] text-brand-ink">
                    {r.period_start} → {r.period_end}
                  </td>
                  <td>
                    <StatusChip s={r.status}/>
                  </td>
                  <td className="mono-num text-right text-brand-ink">
                    {r.totals.line_count}
                    {r.totals.missing_rate_count > 0 && (
                      <span className="ml-1 chip chip-amber mono-num text-[10px]" title="Missing rates">
                        <AlertTriangle size={10}/> {r.totals.missing_rate_count}
                      </span>
                    )}
                  </td>
                  <td className="mono-num text-right text-brand-ink">{r.totals.hours_reg.toFixed(1)}h</td>
                  <td className="mono-num text-right text-brand-muted">{r.totals.hours_ot.toFixed(1)}h</td>
                  <td className="mono-num text-right font-semibold text-brand-ink">{fmtCents(r.totals.gross_cents)}</td>
                  <td className="text-right" onClick={(e)=>e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <a href={`${BACKEND}/api/companies/${companyId}/pay/runs/${r.id}/csv`}
                         target="_blank" rel="noreferrer"
                         className="btn-ghost !h-7 !px-2" title="Export CSV"
                         data-testid={`pay-run-csv-${r.id}`}>
                        <Download size={12}/>
                      </a>
                      <button className="btn-ghost !h-7 !px-2" onClick={() => setOpen(r)}
                              data-testid={`pay-run-open-${r.id}`}>Open</button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-0">
                  <EmptyState icon={PlayCircle} eyebrow="Runs"
                    title="No runs yet."
                    body="Create your first pay run for the current week."
                    action={<button className="btn-violet" onClick={newRun} disabled={creating}
                                    data-testid="pay-empty-new">
                              <Plus size={13}/> New run (this week)
                            </button>}
                    testid="pay-empty" />
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RunDetailDrawer open={!!open} onOpenChange={(v)=>{if(!v) setOpen(null);}}
                       run={open} companyId={companyId} canApprove={canApprove}
                       onChanged={() => { load(); }} />
    </div>
  );
}

function StatusChip({ s }) {
  if (s === "approved") return <span className="chip chip-emerald mono-num text-[10px]"><Lock size={10}/> approved</span>;
  if (s === "locked") return <span className="chip mono-num text-[10px]"><Lock size={10}/> locked</span>;
  return <span className="chip chip-violet mono-num text-[10px]">draft</span>;
}

// ---------------- MY PAY ----------------

function MyPay() {
  const { companyId } = useParams();
  const [data, setData] = React.useState(null);
  const [v2Slips, setV2Slips] = React.useState(null);

  React.useEffect(() => {
    api.get(`/companies/${companyId}/pay/my`)
      .then(({data}) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
    api.get(`/companies/${companyId}/pay/v2/payslips/mine`)
      .then(({data}) => setV2Slips(data.payslips))
      .catch(() => setV2Slips([]));   // pay v2 may not have any slips yet — non-fatal
  }, [companyId]);

  if (!data) return (
    <div className="card-panel overflow-hidden" data-testid="pay-my-loading">
      <SkeletonTable rows={3} cols={5} />
    </div>
  );

  const runsById = Object.fromEntries((data.runs || []).map(r => [r.id, r]));
  const totalGross = data.lines.reduce((a, l) => a + (l.gross_cents || 0), 0);
  const totalHrs = data.lines.reduce((a, l) => a + (l.hours_reg + l.hours_ot), 0);

  return (
    <div data-testid="pay-my">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatTile label="Approved runs" value={data.runs.length} />
        <StatTile label="Total hours" value={`${totalHrs.toFixed(1)}h`} />
        <StatTile label="Total gross" value={fmtCents(totalGross)} highlight />
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="pay-my-table">
            <thead>
              <tr>
                <th style={{width:"28%"}}>Period</th>
                <th style={{width:"14%"}} className="text-right">Reg h</th>
                <th style={{width:"12%"}} className="text-right">OT h</th>
                <th style={{width:"14%"}} className="text-right">Rate</th>
                <th className="text-right">Gross</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.length === 0 && (
                <tr><td colSpan={5} className="p-0">
                  <EmptyState icon={Wallet} eyebrow="Pay"
                    title="No approved pay yet."
                    body="Once your manager approves a run, your lines show up here."
                    testid="pay-my-empty" />
                </td></tr>
              )}
              {data.lines.map((l) => {
                const r = runsById[l.run_id];
                return (
                  <tr key={l.id} data-testid={`pay-my-row-${l.id}`}>
                    <td className="mono-num text-[12px] text-brand-ink">
                      {r ? `${r.period_start} → ${r.period_end}` : "—"}
                    </td>
                    <td className="mono-num text-right">{l.hours_reg.toFixed(2)}</td>
                    <td className="mono-num text-right text-brand-muted">{l.hours_ot.toFixed(2)}</td>
                    <td className="mono-num text-right text-brand-muted">
                      {l.hourly_rate_cents ? fmtCents(l.hourly_rate_cents) : "—"}
                    </td>
                    <td className="mono-num text-right font-semibold text-brand-ink">{fmtCents(l.gross_cents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase 16.2 — Pay v2 payslips (versioned, drill-through). */}
      {v2Slips && v2Slips.length > 0 && (
        <div className="mt-6" data-testid="pay-my-v2-payslips">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[13px] font-semibold text-brand-ink">Payslips</div>
            <span className="chip chip-violet mono-num text-[9.5px]">pay v2</span>
          </div>
          <div className="card-panel overflow-hidden">
            <table className="fable-table" data-testid="pay-my-v2-payslips-table">
              <thead>
                <tr>
                  <th style={{width:"28%"}}>Period</th>
                  <th style={{width:"12%"}} className="text-right">Version</th>
                  <th style={{width:"18%"}} className="text-right">Gross</th>
                  <th style={{width:"18%"}} className="text-right">YTD</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {v2Slips.map(s => (
                  <tr key={s.id} data-testid={`pay-my-v2-slip-${s.id}`}>
                    <td className="mono-num text-[12px] text-brand-ink">
                      {s.period_start} → {s.period_end}
                    </td>
                    <td className="mono-num text-right text-brand-muted">
                      v{s.version}
                    </td>
                    <td className="mono-num text-right font-semibold text-brand-ink">
                      {fmtCents(s.gross_pay_cents)}
                    </td>
                    <td className="mono-num text-right text-brand-muted">
                      {fmtCents(s.ytd_gross_cents)}
                    </td>
                    <td className="text-right">
                      <a href={`${BACKEND}/api/companies/${companyId}/pay/v2/payslips/${s.id}/print`}
                         target="_blank" rel="noreferrer"
                         className="btn-ghost !h-7 !px-2 text-[11px] inline-flex items-center gap-1"
                         data-testid={`pay-my-v2-slip-open-${s.id}`}>
                        View <ChevronRight size={11} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, highlight }) {
  return (
    <div className={`card-panel px-4 py-3 ${highlight ? 'border-brand-violet/25 shadow-card' : ''}`}>
      <div className="micro-label">{label}</div>
      <div className={`mt-1 mono-num text-[22px] font-bold ${highlight ? 'text-brand-violet' : 'text-brand-ink'}`}>
        {value}
      </div>
    </div>
  );
}

// ---------------- SETTINGS ----------------

function PaySettings() {
  const { companyId } = useParams();
  const [f, setF] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/pay/settings`);
      setF(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  if (!f) return <div className="text-[12px] text-brand-muted" data-testid="pay-settings-loading">Loading…</div>;

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/companies/${companyId}/pay/settings`, {
        period_type: f.period_type,
        week_start_day: Number(f.week_start_day),
        ot_threshold_hours: Number(f.ot_threshold_hours),
        ot_multiplier: Number(f.ot_multiplier),
        rounding_minutes: Number(f.rounding_minutes),
      });
      setF(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  return (
    <div className="max-w-[720px]" data-testid="pay-settings">
      <div className="card-panel">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Pay engine</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Period, overtime, rounding</div>
          </div>
          <button className="btn-violet" onClick={save} disabled={saving} data-testid="pay-settings-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="card-panel-body space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="micro-label">Period type</label>
              <select className="field-input mt-1" value={f.period_type}
                      onChange={(e)=>setF(x=>({...x, period_type:e.target.value}))}
                      data-testid="pay-set-period">
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="micro-label">Week starts</label>
              <select className="field-input mt-1" value={f.week_start_day}
                      onChange={(e)=>setF(x=>({...x, week_start_day:e.target.value}))}
                      data-testid="pay-set-weekstart">
                {days.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="micro-label">OT threshold (hours/week)</label>
              <input type="number" step="0.5" className="field-input mt-1 mono-num" value={f.ot_threshold_hours}
                     onChange={(e)=>setF(x=>({...x, ot_threshold_hours:e.target.value}))}
                     data-testid="pay-set-ot-th"/>
            </div>
            <div>
              <label className="micro-label">OT multiplier</label>
              <input type="number" step="0.05" min="1" max="5" className="field-input mt-1 mono-num" value={f.ot_multiplier}
                     onChange={(e)=>setF(x=>({...x, ot_multiplier:e.target.value}))}
                     data-testid="pay-set-ot-mult"/>
            </div>
          </div>
          <div>
            <label className="micro-label">Rounding (minutes)</label>
            <select className="field-input mt-1 mono-num" value={f.rounding_minutes}
                    onChange={(e)=>setF(x=>({...x, rounding_minutes:e.target.value}))}
                    data-testid="pay-set-round">
              {[1,5,10,15,30].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-4 text-[11.5px] text-brand-muted flex items-start gap-2">
        <CheckCircle2 size={13} className="text-brand-emerald shrink-0 mt-0.5"/>
        <div>Pay computes <b>gross</b> only. Tax, NI, deductions and pensions are out of scope until Fable Payroll ships.</div>
      </div>
    </div>
  );
}
